import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Layers,
  CheckCircle2,
  Circle,
  Play,
  Pause,
  AlertTriangle,
  Clock,
  User,
  Package,
  Printer,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePlybookExecution,
  useStartPlybookExecution,
  useConfirmPly,
  usePausePlybookExecution,
  usePlybook,
} from '@/hooks/usePlybooks';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import {
  PLY_COLORS,
  MATERIAL_TYPE_LABELS,
  EXECUTION_STATUS_LABELS,
  EXECUTION_STATUS_COLORS,
  formatFiberAngle,
} from '@/types/plybook';
import type { PlybookPly, PlybookExecution } from '@/types/plybook';
import { cn } from '@/lib/utils';
import { PrintLabelDialog } from '@/components/Labels/PrintLabelDialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PlybookExecutionViewProps {
  workOrderId: string;
  plybookId: string;
  onComplete?: () => void;
}

export function PlybookExecutionView({
  workOrderId,
  plybookId,
  onComplete,
}: PlybookExecutionViewProps) {
  const { user } = useAuth();
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    ply?: PlybookPly;
  }>({ open: false });
  const [showPrintLabelDialog, setShowPrintLabelDialog] = useState(false);
  const [confirmNotes, setConfirmNotes] = useState('');
  const [selectedPlotterCut, setSelectedPlotterCut] = useState<string>('');

  // Fetch execution state
  const { data: execution, isLoading: executionLoading } = usePlybookExecution(workOrderId);

  // Fetch plybook details if no execution yet
  const { data: plybook, isLoading: plybookLoading } = usePlybook(plybookId);

  // Fetch available plotter cuts for traceability
  const { data: plotterCuts } = useQuery({
    queryKey: ['plotter-cuts-available', workOrderId],
    queryFn: async () => {
      const { data, error } = await api
        .from('plotter_cuts')
        .select('id, package_code, batch_number, primary_material')
        .in('status', ['available', 'reserved', 'in_use'])
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
  });

  // Mutations
  const startMutation = useStartPlybookExecution();
  const confirmMutation = useConfirmPly();
  const pauseMutation = usePausePlybookExecution();

  const isLoading = executionLoading || plybookLoading;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-muted rounded w-1/3" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const activePlybook = execution?.plybook || plybook;
  const plies = activePlybook?.plies || [];
  const confirmedPlyNumbers = new Set(
    execution?.confirmations?.map((c) => c.ply_number) || []
  );

  const progress = execution
    ? (execution.confirmed_plies / execution.total_plies) * 100
    : 0;

  const handleStart = async () => {
    if (!user) return;
    await startMutation.mutateAsync({
      workOrderId,
      plybookId,
      operatorId: user.id,
    });
  };

  const handleConfirmPly = async () => {
    if (!user || !confirmDialog.ply || !execution) return;

    await confirmMutation.mutateAsync({
      executionId: execution.id,
      plyId: confirmDialog.ply.id,
      plyNumber: confirmDialog.ply.ply_number,
      operatorId: user.id,
      plotterCutId: selectedPlotterCut || undefined,
      notes: confirmNotes || undefined,
    });

    setConfirmDialog({ open: false });
    setConfirmNotes('');
    setSelectedPlotterCut('');

    // Check if completed
    if (execution.confirmed_plies + 1 >= execution.total_plies) {
      onComplete?.();
    }
  };

  const handlePause = async () => {
    if (!execution) return;
    await pauseMutation.mutateAsync(execution.id);
  };

  const getNextPly = (): PlybookPly | undefined => {
    return plies.find((ply) => !confirmedPlyNumbers.has(ply.ply_number));
  };

  const nextPly = getNextPly();

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Laminado: {activePlybook?.name}
          </CardTitle>
          {execution && (
            <Badge className={EXECUTION_STATUS_COLORS[execution.status]}>
              {EXECUTION_STATUS_LABELS[execution.status]}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress */}
        {execution && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progreso</span>
              <span className="font-medium">
                {execution.confirmed_plies} / {execution.total_plies} capas
              </span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>
        )}

        {/* Not started state */}
        {!execution && (
          <div className="text-center py-6">
            <Layers className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground mb-4">
              Este plybook tiene {plies.length} capas por laminar
            </p>
            <Button onClick={handleStart} disabled={startMutation.isPending} className="gap-2">
              <Play className="w-4 h-4" />
              Iniciar Laminado
            </Button>
          </div>
        )}

        {/* In progress state */}
        {execution && execution.status !== 'completed' && (
          <>
            {/* Next ply highlight */}
            {nextPly && (
              <Card className="border-primary border-2 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-primary">
                      Siguiente capa a colocar:
                    </span>
                    <Badge variant="outline" className="text-lg px-3">
                      #{nextPly.ply_number}
                    </Badge>
                  </div>

                  <div className="flex items-start gap-4">
                    <div
                      className="w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-xl shadow"
                      style={{
                        backgroundColor:
                          nextPly.color ||
                          PLY_COLORS[nextPly.material_type || 'other'],
                      }}
                    >
                      {nextPly.ply_number}
                    </div>

                    <div className="flex-1 space-y-1">
                      <p className="font-medium">
                        {nextPly.material_name ||
                          MATERIAL_TYPE_LABELS[nextPly.material_type || 'other']}
                      </p>
                      <div className="flex flex-wrap gap-2 text-sm">
                        <Badge variant="secondary">{formatFiberAngle(nextPly.fiber_angle)}</Badge>
                        {nextPly.width_mm && nextPly.length_mm && (
                          <span className="text-muted-foreground">
                            {nextPly.width_mm} x {nextPly.length_mm} mm
                          </span>
                        )}
                      </div>
                      {nextPly.special_instructions && (
                        <p className="text-sm text-amber-600 flex items-center gap-1 mt-2">
                          <AlertTriangle className="w-4 h-4" />
                          {nextPly.special_instructions}
                        </p>
                      )}
                    </div>

                    <Button
                      size="lg"
                      onClick={() => setConfirmDialog({ open: true, ply: nextPly })}
                      className="gap-2"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      Confirmar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ply list */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">
                Todas las capas:
              </h4>
              <div className="grid gap-2 max-h-64 overflow-y-auto">
                {plies.map((ply) => {
                  const isConfirmed = confirmedPlyNumbers.has(ply.ply_number);
                  const isNext = ply.id === nextPly?.id;

                  return (
                    <div
                      key={ply.id}
                      className={cn(
                        'flex items-center gap-3 p-2 rounded-lg border',
                        isConfirmed && 'bg-green-50 border-green-200',
                        isNext && 'border-primary bg-primary/5',
                        !isConfirmed && !isNext && 'bg-muted/30'
                      )}
                    >
                      <div
                        className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold"
                        style={{
                          backgroundColor:
                            ply.color || PLY_COLORS[ply.material_type || 'other'],
                        }}
                      >
                        {ply.ply_number}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">
                          {ply.material_name ||
                            MATERIAL_TYPE_LABELS[ply.material_type || 'other']}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFiberAngle(ply.fiber_angle)}
                        </p>
                      </div>

                      {isConfirmed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground/30" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pause button */}
            {execution.status === 'in_progress' && (
              <Button
                variant="outline"
                onClick={handlePause}
                disabled={pauseMutation.isPending}
                className="w-full gap-2"
              >
                <Pause className="w-4 h-4" />
                Pausar Laminado
              </Button>
            )}
          </>
        )}

        {/* Completed state */}
        {execution?.status === 'completed' && (
          <div className="text-center py-6">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-600 mb-3" />
            <p className="text-lg font-medium text-green-600">
              Laminado Completado
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {execution.total_plies} capas confirmadas
            </p>
            {execution.completed_at && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
                <Clock className="w-3 h-3" />
                Completado el{' '}
                {format(new Date(execution.completed_at), "d MMM yyyy 'a las' HH:mm", {
                  locale: es,
                })}
              </p>
            )}

            {/* Print Label Button */}
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setShowPrintLabelDialog(true)}
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir Etiqueta QR
            </Button>
          </div>
        )}
      </CardContent>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Capa #{confirmDialog.ply?.ply_number}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Ply details */}
            {confirmDialog.ply && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{
                    backgroundColor:
                      confirmDialog.ply.color ||
                      PLY_COLORS[confirmDialog.ply.material_type || 'other'],
                  }}
                >
                  {confirmDialog.ply.ply_number}
                </div>
                <div>
                  <p className="font-medium">
                    {confirmDialog.ply.material_name ||
                      MATERIAL_TYPE_LABELS[confirmDialog.ply.material_type || 'other']}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Ángulo: {formatFiberAngle(confirmDialog.ply.fiber_angle)}
                  </p>
                </div>
              </div>
            )}

            {/* Plotter cut selector (traceability) */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Package className="w-4 h-4" />
                Paquete de Corte (trazabilidad)
              </label>
              <Select
                value={selectedPlotterCut || 'none'}
                onValueChange={(v) => setSelectedPlotterCut(v === 'none' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar paquete..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin especificar</SelectItem>
                  {plotterCuts?.map((pc) => (
                    <SelectItem key={pc.id} value={pc.id}>
                      {pc.package_code}
                      {pc.batch_number && ` - Lote: ${pc.batch_number}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Notas (opcional)</label>
              <Textarea
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                placeholder="Observaciones sobre esta capa..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false })}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmPly}
              disabled={confirmMutation.isPending}
              className="gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              Confirmar Capa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Label Dialog */}
      {execution && (
        <PrintLabelDialog
          open={showPrintLabelDialog}
          onOpenChange={setShowPrintLabelDialog}
          labelType="lamination"
          entityId={execution.id}
          entityName={plybook?.name || 'Laminado'}
          plybookExecution={{
            id: execution.id,
            work_order_id: workOrderId,
            status: execution.status,
            total_plies: execution.total_plies,
            confirmed_plies: execution.confirmed_plies,
            started_at: execution.started_at,
            completed_at: execution.completed_at,
            plybook: plybook ? { code: plybook.code, name: plybook.name } : undefined,
          }}
        />
      )}
    </Card>
  );
}
